import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import { SIDEBAR_MENU_ICONS } from '@/components/AppLayout/consts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';

import { cn } from '@/lib/utils';

import { NavItem, NavItemWithSubItems } from '@/types';

import { useSidebarContext } from '@/providers';

type SidebarNavItemProps = {
  item: NavItem | NavItemWithSubItems;
};

const menuButtonClass = 'text-sidebar-menu-foreground h-8 gap-1.5 text-xs';
const menuButtonHoverClass =
  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground';
const menuButtonActiveClass =
  'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground';

export const SidebarNavItem = ({ item }: SidebarNavItemProps) => {
  const { t } = useTranslation();
  const { state: sidebarState } = useSidebarContext();
  const IconComponent =
    SIDEBAR_MENU_ICONS[item.icon as keyof typeof SIDEBAR_MENU_ICONS] ||
    SIDEBAR_MENU_ICONS['Default'];

  const isSubmenu = 'submenu' in item;
  const initialOpen = isSubmenu
    ? item.submenu.some((submenuItem) => submenuItem.active)
    : false;
  const [submenuOpen, setSubmenuOpen] = useState(initialOpen);

  const onSubmenuOpenChange = (next: boolean) => {
    setSubmenuOpen(next);
  };

  if (isSubmenu && sidebarState === 'collapsed') {
    const isActive = item.submenu.some((submenuItem) => submenuItem.active);

    return (
      <Fragment>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                tooltip={t(item.text)}
                className={cn(
                  menuButtonClass,
                  menuButtonHoverClass,
                  'cursor-pointer',
                  isActive && menuButtonActiveClass,
                )}
              >
                <IconComponent className="size-4 shrink-0" />
                <span className="whitespace-nowrap">{t(item.text)}</span>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="start"
              sideOffset={8}
              className="min-w-48"
            >
              {item.submenu.map((submenuItem) => (
                <DropdownMenuItem
                  asChild
                  key={submenuItem.href + submenuItem.text}
                  className={cn(
                    'text-sidebar-menu-foreground',
                    submenuItem.active && menuButtonActiveClass,
                  )}
                >
                  <a
                    href={submenuItem.href}
                    {...(submenuItem.active ? { 'data-nav-active': true } : {})}
                  >
                    {t(submenuItem.text)}
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
        {item.separator && <SidebarSeparator className="mx-0 my-0.5" />}
      </Fragment>
    );
  }

  if (isSubmenu) {
    return (
      <Fragment>
        <Collapsible
          asChild
          open={submenuOpen}
          onOpenChange={onSubmenuOpenChange}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip={t(item.text)}
                className={cn(
                  menuButtonClass,
                  menuButtonHoverClass,
                  'cursor-pointer',
                )}
              >
                <IconComponent className="size-4 shrink-0" />
                <span className="whitespace-nowrap">{t(item.text)}</span>
                <ChevronRight className="ml-auto transition-transform group-data-[collapsible=icon]:hidden group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent className="group-data-[collapsible=icon]:hidden">
              <SidebarMenuSub className="mx-2 gap-0.5 border-neutral-400 px-1.5 py-0">
                {item.submenu.map((submenuItem) => (
                  <SidebarMenuSubItem key={submenuItem.href + submenuItem.text}>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      className={cn(
                        'text-sidebar-menu-foreground h-auto min-h-6 items-start px-1.5 py-1',
                        '[&>span:last-child]:line-clamp-3 [&>span:last-child]:whitespace-normal',
                        menuButtonHoverClass,
                        submenuItem.active && menuButtonActiveClass,
                      )}
                    >
                      <a
                        href={submenuItem.href}
                        className="min-w-0"
                        {...(submenuItem.active
                          ? { 'data-nav-active': true }
                          : {})}
                      >
                        <span className="line-clamp-3 whitespace-normal">
                          {t(submenuItem.text)}
                        </span>
                      </a>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
        {item.separator && <SidebarSeparator className="mx-0 my-0.5" />}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={t(item.text)}
          className={cn(
            menuButtonClass,
            menuButtonHoverClass,
            item.active && menuButtonActiveClass,
          )}
        >
          <a
            href={item.href}
            className="flex min-w-0 items-center gap-1.5"
          >
            <IconComponent className="size-4 shrink-0" />
            <span>{t(item.text)}</span>
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {item.separator && <SidebarSeparator className="mx-0 my-0.5" />}
    </Fragment>
  );
};
